import { useCallback } from "react";

export const useRetrospectiveRendering = (
  cursors: Map<string, any>,
  draggingNodes: Map<string, any>,
  activeConnections: Map<string, any>,
  nodes: any[],
  users: Map<string, any>,
  viewport: any,
  reactFlowInstance: any
) => {
  // Return data for cursors instead of JSX
  const getCursorsData = useCallback(() => {
    if (!viewport || !cursors) return [];

    return Array.from(cursors.values())
      .map((cursor) => {
        if (!cursor) return null;

        const screenX = cursor.x * viewport.zoom + viewport.x;
        const screenY = cursor.y * viewport.zoom + viewport.y;

        return {
          key: cursor.userId,
          x: screenX,
          y: screenY,
          userName: cursor.userName,
          color: cursor.color,
        };
      })
      .filter(Boolean);
  }, [cursors, viewport]);

  // Return data for dragging previews instead of JSX
  const getDraggingPreviewsData = useCallback(() => {
    if (!viewport || !draggingNodes) return [];

    return Array.from(draggingNodes.values())
      .map((dragging) => {
        if (!dragging) return null;

        const node = nodes.find((n) => n.id === dragging.nodeId);
        if (!node) return null;

        const screenX = dragging.position.x * viewport.zoom + viewport.x;
        const screenY = dragging.position.y * viewport.zoom + viewport.y;
        const user = users.get(dragging.userId);

        return {
          key: `${dragging.nodeId}-${dragging.userId}`,
          x: screenX,
          y: screenY,
          width: (node.style?.width || 120) * viewport.zoom,
          height: (node.style?.height || 120) * viewport.zoom,
          color: user?.color || "#999",
          userName: user?.name || "Unknown",
          shape: node.data?.shape || "rectangle",
        };
      })
      .filter(Boolean);
  }, [draggingNodes, nodes, users, viewport]);

  // Return data for connection previews instead of JSX
  const getConnectionPreviewsData = useCallback(() => {
    if (!viewport || !activeConnections) return [];

    return Array.from(activeConnections.values()).map((connection) => {
      const fromX = connection.from.x * viewport.zoom + viewport.x;
      const fromY = connection.from.y * viewport.zoom + viewport.y;
      const toX = connection.to.x * viewport.zoom + viewport.x;
      const toY = connection.to.y * viewport.zoom + viewport.y;

      const user = users.get(connection.userId);

      return {
        key: connection.userId,
        fromX,
        fromY,
        toX,
        toY,
        color: user?.color || "#999",
      };
    });
  }, [activeConnections, users, viewport]);

  return {
    getCursorsData,
    getDraggingPreviewsData,
    getConnectionPreviewsData,
  };
};
